import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AuthProviderType } from '@terra/shared-authorization';
import type { AuthContextValue, AuthUser } from '../app/providers/auth/types';

const ELECTRON_OPENID_ACCESS_TOKEN_KEY = 'terra.electron.access_token';

function clearSessionToken(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(ELECTRON_OPENID_ACCESS_TOKEN_KEY);
}

function setSessionToken(token: string): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(ELECTRON_OPENID_ACCESS_TOKEN_KEY, token.trim());
}

async function loadUserProfileFromElectron(): Promise<AuthUser | null> {
  if (!window.electronAPI?.auth) return null;

  const profileResult = await window.electronAPI.auth.getProfile();
  if (!profileResult?.success || !profileResult.profile) return null;

  const profile = profileResult.profile;
  return {
    userId: profile.sub,
    email: profile.email,
    name: profile.name,
    picture: profile.picture,
  };
}

async function loadAccessTokenFromElectron(): Promise<string | null> {
  if (!window.electronAPI?.auth) return null;

  const tokenResult = await window.electronAPI.auth.getAccessToken();
  if (!tokenResult?.success || !tokenResult.accessToken) return null;

  return tokenResult.accessToken;
}

export function useElectronAuth({
  providerType,
}: {
  providerType: AuthProviderType;
}): {
  contextValue: AuthContextValue;
} {
  const [isElectronReady, setIsElectronReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isTokenReady, setIsTokenReady] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);

  const checkAuthentication = useCallback(async () => {
    if (!window.electronAPI?.auth) {
      setIsAuthenticated(false);
      setIsTokenReady(false);
      setUser(null);
      clearSessionToken();
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const result = await window.electronAPI.auth.isAuthenticated();

      if (result.authenticated) {
        const profile = await loadUserProfileFromElectron();
        setUser(profile);

        const token = await loadAccessTokenFromElectron();
        if (token) {
          setSessionToken(token);
          setIsTokenReady(true);
        } else {
          setIsTokenReady(false);
        }
        setIsAuthenticated(true);
      } else {
        setUser(null);
        clearSessionToken();
        setIsTokenReady(false);
        setIsAuthenticated(false);
      }
    } catch {
      setIsAuthenticated(false);
      setIsTokenReady(false);
      setUser(null);
      clearSessionToken();
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hasAuthApi = !!window.electronAPI?.auth;
    if (hasAuthApi) {
      setIsElectronReady(true);
      checkAuthentication();
      return;
    }

    const cleanup = window.electronAPI?.onElectronReady?.(() => {
      setIsElectronReady(true);
      checkAuthentication();
    });

    return () => {
      cleanup?.();
    };
  }, [checkAuthentication]);

  useEffect(() => {
    if (!isElectronReady || !window.electronAPI?.auth) return;

    const cleanupSuccess = window.electronAPI.auth.onAuthSuccess(async () => {
      await checkAuthentication();
    });

    const cleanupError = window.electronAPI.auth.onAuthError(() => {
      setIsAuthenticated(false);
      setIsTokenReady(false);
      setUser(null);
      setIsLoading(false);
      clearSessionToken();
    });

    return () => {
      cleanupSuccess?.();
      cleanupError?.();
    };
  }, [checkAuthentication, isElectronReady]);

  const logout = useCallback(async () => {
    if (!window.electronAPI?.auth) return;

    await window.electronAPI.auth.logout();
    setIsAuthenticated(false);
    setIsTokenReady(false);
    setUser(null);
    clearSessionToken();
  }, []);

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isLoading:
        isLoading || !isElectronReady || (isAuthenticated && !isTokenReady),
      user,
      logout,
      providerType,
    }),
    [
      isAuthenticated,
      isElectronReady,
      isLoading,
      isTokenReady,
      user,
      logout,
      providerType,
    ],
  );

  return { contextValue };
}

export function useElectronOpenIdAuth(): {
  contextValue: AuthContextValue;
} {
  return useElectronAuth({ providerType: 'openid' });
}
